import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import './ProfilePage.css';
import { ProgressiveImage } from './ProgressiveImage';
import { Post as PostData, Photo as PhotoData } from './HomePage';
import { DEFAULT_PROFILE_PICTURE } from '../constants/defaults';
import PostCard from './PostCard';
import PostModal from './PostModal';
import { useAuth } from '../contexts/AuthContext';
import FollowListModal from './FollowListModal';
import { List } from '../types/list';

interface UserPost extends PostData {}

interface PublicUserProfile {
  id: string;
  username: string;
  created_at: string;
  metadata: { 
    display_name?: string;
    firebase_info?: {
      name?: string;
      picture?: string;
    };
  };
  followerCount: number;
  followingCount: number;
  isFollowedByCurrentUser?: boolean;
}

interface FollowerUser {
  id: string;
  username: string;
  user_display_name?: string;
  picture?: string;
  metadata?: { 
    display_name?: string;
    firebase_info?: { picture?: string };
  };
}

type ActiveTab = 'posts' | 'lists';

const ProfilePostCard: React.FC<{ post: UserPost }> = ({ post }) => {
  let image: PhotoData | undefined = post.first_photo;

  return (
    <div className="post-card profile-post-card">
      <div className="post-header">
        <div className="post-info">
          <span className="post-title">{post.title}</span>
          <span className="post-date">
            {new Date(post.visit_date || post.created_at).toLocaleDateString()}
          </span>
          {post.restaurant_name && (
            <div className="restaurant-info">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="location-icon">
                <path fillRule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.757.433.36.36 0 00.28.14l.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clipRule="evenodd" />
              </svg>
              <span>{post.restaurant_name}</span>
            </div>
          )}
        </div>
      </div>
      {image && (
        <div className="post-image-container">
          <ProgressiveImage
            src={`${API_BASE_URL}/api/posts/images/${image.displayUrl}`}
            thumbnailSrc={`${API_BASE_URL}/api/posts/images/${image.thumbnailUrl}`}
            alt={image.name || post.title || 'Post image'}
            className="post-image"
          />
        </div>
      )}
      <div className="post-content">
        <p className="post-text">{post.content}</p>
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="profile-container">
    <div className="profile-header">
      <div className="skeleton skeleton-avatar" />
      <div className="skeleton skeleton-text" style={{ width: '200px', margin: '0 auto' }} />
      <div className="skeleton skeleton-text" style={{ width: '150px', margin: '0 auto' }} />
    </div>
    <div className="profile-stats">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="stat-item">
          <div className="skeleton skeleton-text" style={{ width: '50px', margin: '0 auto' }} />
          <div className="skeleton skeleton-text" style={{ width: '80px', margin: '0.5rem auto 0' }} />
        </div>
      ))}
    </div>
    <div className="profile-content">
      <div className="posts-grid">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="skeleton skeleton-card" />
        ))}
      </div>
    </div>
  </div>
);

const UserProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<PublicUserProfile | null>(null);
  const [userPosts, setUserPosts] = useState<PostData[]>([]);
  const [loading, setLoading] = useState({ profile: true, posts: true, followAction: false, followList: false });
  const [error, setError] = useState('');
  const [selectedPost, setSelectedPost] = useState<PostData | null>(null);
  
  const [isFollowModalOpen, setIsFollowModalOpen] = useState(false);
  const [followListTitle, setFollowListTitle] = useState('');
  const [followListUsers, setFollowListUsers] = useState<FollowerUser[] | null>(null);
  const [followListError, setFollowListError] = useState<string | null>(null);
  
  const [activeTab, setActiveTab] = useState<ActiveTab>('posts');
  const [publicLists, setPublicLists] = useState<List[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  
  const fetchUserProfile = useCallback(async () => {
    if (!username) return;
    setLoading(prev => ({ ...prev, profile: true }));
    setError('');
    
    try {
      const token = currentUser ? await currentUser.getIdToken() : null;
      const response = await fetch(`${API_ENDPOINTS.USERS}/profile/u/${username}`, {
         headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        if (response.status === 404) throw new Error('User profile not found');
        throw new Error(`Failed to fetch user profile (${response.status})`);
      }
      const profileData: PublicUserProfile = await response.json();
      setUserProfile(profileData);
      
      console.log("Fetched profile data with follow info:", profileData); 
      
      return profileData.id;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user profile');
      console.error('Error fetching user profile:', err);
      setUserProfile(null);
      return null;
    } finally {
      setLoading(prev => ({ ...prev, profile: false }));
    }
  }, [username, currentUser]);

  const fetchUserPosts = useCallback(async (userId: string | null) => {
    if (!userId) {
      setLoading(prev => ({ ...prev, posts: false }));
      return;
    }
    setLoading(prev => ({ ...prev, posts: true }));
    try {
      const postsResponse = await fetch(`${API_ENDPOINTS.POSTS}?user_id=${userId}`);
      if (!postsResponse.ok) {
        console.error('Failed to fetch user posts');
        setError(prev => prev || 'Failed to fetch user posts');
      } else {
        const postsData: PostData[] = await postsResponse.json();
        setUserPosts(postsData);
      }
    } catch (err) {
      console.error('Error fetching user posts:', err);
      setError(prev => prev || (err instanceof Error ? err.message : 'Failed to load posts'));
    } finally {
      setLoading(prev => ({ ...prev, posts: false }));
    }
  }, []);

  const fetchPublicLists = useCallback(async (userId: string) => {
    setLoadingLists(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/lists/user/${userId}/public`);
      if (!response.ok) {
        throw new Error(`Failed to fetch public lists (${response.status})`);
      }
      const data = await response.json();
      setPublicLists(data);
    } catch (err) {
      console.error('Error fetching public lists:', err);
      setError(prev => prev || (err instanceof Error ? err.message : 'Failed to load public lists'));
      setPublicLists([]);
    } finally {
      setLoadingLists(false);
    }
  }, []);

  useEffect(() => {
    const orchestrateFetch = async () => {
      setLoading(prev => ({ ...prev, profile: true, posts: true }));
      setUserPosts([]);
      const fetchedUserId = await fetchUserProfile();
      if (fetchedUserId) { 
        await Promise.all([
          fetchUserPosts(fetchedUserId),
          fetchPublicLists(fetchedUserId)
        ]);
      } else {
        setLoading(prev => ({ ...prev, posts: false }));
      }
    };
    orchestrateFetch();
  }, [username, fetchUserProfile, fetchUserPosts, fetchPublicLists]);

  const handlePostClick = (post: PostData) => {
    setSelectedPost(post);
  };

  const handleCloseModal = () => {
    setSelectedPost(null);
  };

  const handleFollowToggle = async () => {
    if (!userProfile || !currentUser || loading.followAction) return;
    
    const currentIsFollowing = userProfile.isFollowedByCurrentUser ?? false;
    const action = currentIsFollowing ? 'DELETE' : 'POST';
    const optimisticProfileData = { 
        ...userProfile, 
        isFollowedByCurrentUser: !currentIsFollowing, 
        followerCount: userProfile.followerCount + (!currentIsFollowing ? 1 : -1)
    };

    setLoading(prev => ({ ...prev, followAction: true }));
    setUserProfile(optimisticProfileData);
    setError('');

    try {
      const token = await currentUser.getIdToken();
      if (!token) throw new Error("Authentication token not found.");

      const response = await fetch(API_ENDPOINTS.followUser(userProfile.id), {
        method: action,
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `Failed to ${action === 'POST' ? 'follow' : 'unfollow'}` }));
        throw new Error(errorData.error || `Failed to ${action === 'POST' ? 'follow' : 'unfollow'}`);
      }
      
      console.log(`Successfully ${action === 'POST' ? 'followed' : 'unfollowed'} user.`);

    } catch (err) {
      console.error(`${action === 'POST' ? 'Follow' : 'Unfollow'} error:`, err);
      setError(err instanceof Error ? err.message : 'Action failed');
      setUserProfile(userProfile);
    } finally {
      setLoading(prev => ({ ...prev, followAction: false }));
    }
  };

  const fetchFollowList = async (type: 'followers' | 'following') => {
    if (!userProfile) return;

    setIsFollowModalOpen(true);
    setFollowListTitle(type === 'followers' ? 'Followers' : 'Following');
    setFollowListUsers(null); 
    setFollowListError(null);
    setLoading(prev => ({ ...prev, followList: true }));

    try {
        const token = currentUser ? await currentUser.getIdToken() : null;
        const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};

        const endpoint = type === 'followers' 
            ? API_ENDPOINTS.getUserFollowers(userProfile.id)
            : API_ENDPOINTS.getUserFollowing(userProfile.id);

        const response = await fetch(endpoint, { headers });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: `Failed to fetch ${type}` }));
            throw new Error(errorData.error || `Failed to fetch ${type}`);
        }

        const data: FollowerUser[] = await response.json();
        
        // Parse metadata string if it exists and ensure proper structure
        const parsedData = data.map(user => {
            let parsedMetadata: FollowerUser['metadata'] = {
                firebase_info: {}
            };
            if (typeof user.metadata === 'string') {
                try {
                    const parsed = JSON.parse(user.metadata);
                    parsedMetadata = {
                        ...parsed,
                        firebase_info: parsed.firebase_info || {}
                    };
                } catch (e) {
                    console.error("Failed to parse metadata for user:", user.id, e);
                }
            } else if (typeof user.metadata === 'object' && user.metadata !== null) {
                parsedMetadata = {
                    ...user.metadata,
                    firebase_info: user.metadata.firebase_info || {}
                };
            }

            return {
                ...user,
                metadata: parsedMetadata
            };
        });

        setFollowListUsers(parsedData);

    } catch (err) {
        console.error(`Error fetching ${type}:`, err);
        setFollowListError(err instanceof Error ? err.message : `Could not load ${type}`);
        setFollowListUsers([]); 
    } finally {
        setLoading(prev => ({ ...prev, followList: false }));
    }
  };

  const handleShowFollowers = () => {
      fetchFollowList('followers');
  };
  
  const handleShowFollowing = () => {
      fetchFollowList('following');
  };

  const closeFollowListModal = () => {
      setIsFollowModalOpen(false);
      setFollowListUsers(null);
      setFollowListError(null);
  };

  const followListData = useMemo(() => {
    if (!followListUsers) return null;
    return followListUsers.map(user => ({
      id: user.id,
      username: user.username,
      user_display_name: user.user_display_name || user.username,
      picture: user.picture || DEFAULT_PROFILE_PICTURE
    }));
  }, [followListUsers]);

  if (loading.profile) {
    return <LoadingSkeleton />;
  }

  if (!userProfile) {
    return <div className="error-message">{error || 'User not found.'}</div>;
  }

  const displayUserName = userProfile.metadata?.display_name || userProfile.metadata?.firebase_info?.name || userProfile.username || 'User';
  const avatarUrl = userProfile.metadata?.firebase_info?.picture || DEFAULT_PROFILE_PICTURE;
  const isOwnProfile = currentUser?.uid === userProfile.id;
  const isFollowing = userProfile.isFollowedByCurrentUser ?? false;

  return (
    <div className="profile-page">
      {error && <div className="error-message-banner">{error}</div>}
      
      <div className="profile-container">
        <div className="profile-header">
          <img src={avatarUrl} alt="Profile" className="profile-avatar" />
          <div className="profile-info">
            <div className="profile-info-container">
              <div className="profile-name-follow-container">
                <h1>{displayUserName}</h1>
                {!isOwnProfile && currentUser && ( 
                  <button 
                    onClick={handleFollowToggle}
                    disabled={loading.followAction}
                    className={`follow-button ${isFollowing ? 'following' : ''}`}
                    title={isFollowing ? 'Already following this user' : 'Click to follow'}
                  >
                    {loading.followAction ? 'Processing...' : (isFollowing ? 'Following' : 'Follow')}
                  </button>
                )}
                {!isOwnProfile && !currentUser && (
                  <button className="follow-button disabled" onClick={() => navigate('/login')}>Log in to follow</button>
                )}
              </div>
              {userProfile.username !== displayUserName && (
                <span className="username-display">@{userProfile.username}</span>
              )}
            </div>
          </div>
          <div className="profile-stats">
            <div className="stat-item">
              <span className="stat-number">{userPosts.length}</span>
              <span className="stat-label">Posts</span>
            </div>
            <div className="stat-item clickable" onClick={handleShowFollowers}>
              <span className="stat-number">{userProfile.followerCount}</span>
              <span className="stat-label">Followers</span>
            </div>
            <div className="stat-item clickable" onClick={handleShowFollowing}>
              <span className="stat-number">{userProfile.followingCount}</span>
              <span className="stat-label">Following</span>
            </div>
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-tabs">
            <button
              className={`tab-button ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
            >
              Posts
            </button>
            <button
              className={`tab-button ${activeTab === 'lists' ? 'active' : ''}`}
              onClick={() => setActiveTab('lists')}
            >
              Lists
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {activeTab === 'posts' ? (
            <>
              {loading.posts && <p>Loading posts...</p>}
              {!loading.posts && userPosts.length === 0 && (
                <div className="no-posts">
                  <p>{displayUserName} hasn't posted anything yet.</p>
                </div>
              )}
              {!loading.posts && userPosts.length > 0 && (
                <div className="posts-grid">
                  {userPosts.map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      onCardClick={() => handlePostClick(post)}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="lists-container">
              {loadingLists ? (
                <div className="loading">Loading lists...</div>
              ) : publicLists.length === 0 ? (
                <div className="no-lists">
                  <p>{displayUserName} hasn't created any public lists yet.</p>
                </div>
              ) : (
                <div className="lists-grid">
                  {publicLists.map(list => {
                    const postCount = list.metadata?.items?.filter(item => item.type === 'post').length || 0;
                    const restaurantCount = list.metadata?.items?.filter(item => item.type === 'restaurant').length || 0;
                    
                    return (
                      <div 
                        key={list.id}
                        className="list-card"
                        onClick={() => navigate(`/lists/${list.id}`)}
                      >
                        <div className="list-card-header">
                          <h3>{list.name}</h3>
                        </div>
                        <div className="list-stats">
                          <span>{postCount} posts</span>
                          <span>{restaurantCount} restaurants</span>
                        </div>
                        <div className="list-date">
                          Created {new Date(list.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedPost && (
        <PostModal post={selectedPost} onClose={handleCloseModal} />
      )}

      <FollowListModal 
        isOpen={isFollowModalOpen}
        onClose={closeFollowListModal}
        title={followListTitle}
        users={followListData}
        isLoading={loading.followList}
        error={followListError}
      />
    </div>
  );
};

export default UserProfilePage; 